///
/// EarthBrowser
///
/// Created by Matt Giger on 8/19/2024
/// Copyright (c) 2024 Geodata Labs
///

import { PanelDepths } from "@/earthbrowser/constants/PanelDepths"
import { formatAltitude } from "@/earthbrowser/utils/unitFormatters"
import { animation, easing } from "@stovokor/math/animation"
import { type color, rgba } from "@stovokor/math/color"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { Canvas } from "@stovokor/uipanes/Canvas"
import { Font, FontFamily } from "@stovokor/uipanes/Font"
import { Pane } from "@stovokor/uipanes/Pane"

const cCrosshairAlpha = 0.85
// Pane is wider than the reticle to leave room for the coordinate readout
const cPaneWidth = 260
const cPaneHeight = 84
// Reticle center within the pane; the pane is offset so this point lands
// at the exact center of the viewport
const cReticleCenterY = 20
// Keep the rotation ring up briefly after the last bearing change so it
// doesn't flicker during hesitant rotation gestures
const cRotateHoldMs = 250
// The coordinate readout appears once the camera settles, not while it
// moves (the numbers churn unreadably mid-gesture and a finger covers the
// reticle on touch), and holds briefly: "where did I land?"
const cReadoutHoldSeconds = 1.2
// No readout above this altitude: at globe scale 0.01 degree precision is
// noise and the terrain elevation under the reticle means nothing
const cReadoutMaxAltitudeKm = 1500

const cLabelFont = new Font(FontFamily.sansserif, 14)
const cSublabelFont = new Font(FontFamily.sansserif, 12)

/** Where the camera is looking, sampled each frame it moves */
export interface CrosshairPose {
	lat: number
	lon: number
	/** Rough terrain elevation at the fulcrum, undefined until elevation data has loaded */
	elevationM: number | undefined
	/** Camera altitude above the ellipsoid, for the readout gate */
	altitudeKm: number
	bearing: number
	/** The bearing changed this frame: show the rotation ring */
	rotating: boolean
}

export class Crosshair extends Pane {
	private showing = false
	private xhair = new CrosshairContent()
	private pause = new animation().from(0).to(0).duration(0.1)
	private fadeIn = new animation().from(0).to(1).duration(0.08)
	private fadeOut = new animation().from(0).to(1).duration(0.3).easing(easing.cubicOut)
	private readoutIn = new animation().from(0).to(1).duration(0.15)
	private readoutHold = new animation().from(0).to(0).duration(cReadoutHoldSeconds)
	private readoutOut = new animation().from(1).to(0).duration(0.4).easing(easing.cubicOut)
	private rotateUntil = 0
	private lastPose?: CrosshairPose

	constructor(context: StovokorContext) {
		super(context)

		this.depth = PanelDepths.CROSSHAIR
		this.visible = false
		this.size(cPaneWidth, cPaneHeight)
			.contents(this.xhair)

		// Settled: fade the reticle out and bring the readout in on the
		// resting pose
		this.pause
			.onComplete(() => {
				this.fadeOut.start()
				this.startReadout()
			})

		this.fadeIn
			.onUpdate((value) => this.xhair.alpha = cCrosshairAlpha * value)

		// Settle pulse: the reticle expands slightly while fading out to
		// confirm where the camera landed
		this.fadeOut
			.onUpdate((value) => {
				this.xhair.alpha = cCrosshairAlpha * (1 - value)
				this.xhair.pulse = 1 + 0.35 * value
			})
			.onComplete(() => {
				this.xhair.pulse = 1
				this.updateVisibility()
			})

		this.readoutIn
			.onUpdate((value) => this.xhair.readoutAlpha = cCrosshairAlpha * value)
			.onComplete(() => this.readoutHold.start())
		this.readoutHold
			.onComplete(() => this.readoutOut.start())
		this.readoutOut
			.onUpdate((value) => this.xhair.readoutAlpha = cCrosshairAlpha * value)
			.onComplete(() => this.updateVisibility())
	}

	override parentResized(w: number, h: number, _wdelta: number, _hdelta: number): void {
		this.offset(w / 2 - cPaneWidth / 2, h / 2 - cReticleCenterY)
	}

	/** Show the reticle while the camera is being moved; hide once it stops */
	public show(show: boolean): void {
		if(show && !this.showing) {
			this.showing = true
			this.pause.stop()
			this.fadeOut.stop()
			this.stopReadout()
			this.visible = true
			this.xhair.pulse = 1
			this.fadeIn.start()
		} else if(!show && this.showing) {
			this.showing = false
			this.fadeIn.stop()
			this.pause.start()
		}
	}

	/**
	 * An automated move took over (or an entity took focus): hide the
	 * reticle and drop any readout, including the one that would otherwise
	 * appear when the interrupted gesture's pose settles
	 */
	public dismiss(): void {
		this.lastPose = undefined
		this.stopReadout()
		this.show(false)
		this.updateVisibility()
	}

	/**
	 * Track the camera while it moves: drives the rotation ring and records
	 * the pose the readout shows once the camera settles
	 */
	public setPose(pose: CrosshairPose): void {
		const now = performance.now()

		if(pose.rotating) {
			this.rotateUntil = now + cRotateHoldMs
		}
		this.xhair.showRing = now < this.rotateUntil
		this.xhair.bearing = pose.bearing
		this.lastPose = pose
	}

	private startReadout(): void {
		const pose = this.lastPose
		if(!pose || pose.altitudeKm > cReadoutMaxAltitudeKm) return

		this.xhair.label = formatPose(pose.lat, pose.lon)
		this.xhair.sublabel = pose.elevationM !== undefined ? `≈ ${formatAltitude(pose.elevationM)}` : ""
		this.readoutHold.stop()
		this.readoutOut.stop()
		this.readoutIn.start()
	}

	/** A new gesture started: drop the readout at once, the reticle takes over */
	private stopReadout(): void {
		this.readoutIn.stop()
		this.readoutHold.stop()
		this.readoutOut.stop()
		this.xhair.readoutAlpha = 0
	}

	/** Dev-only diagnostics for window.__ebDebug.getCrosshair() */
	public get stats(): { visible: boolean; reticle: boolean; readout: boolean; label: string; sublabel: string } {
		return {
			visible: this.visible,
			reticle: this.showing,
			readout: this.readoutIn.running || this.readoutHold.running || this.readoutOut.running,
			label: this.xhair.labelText,
			sublabel: this.xhair.sublabelText
		}
	}

	/** Hide the pane once neither the reticle nor the readout has anything left to draw */
	private updateVisibility(): void {
		if(this.showing) return
		if(this.fadeOut.running || this.readoutIn.running || this.readoutHold.running || this.readoutOut.running) return
		this.visible = false
	}
}

function formatPose(lat: number, lon: number): string {
	const ns = lat < 0 ? "S" : "N"
	const ew = lon < 0 ? "W" : "E"
	return `${Math.abs(lat).toFixed(2)}°${ns}  ${Math.abs(lon).toFixed(2)}°${ew}`
}

class CrosshairContent extends Canvas {
	private _alpha = 0
	private _readoutAlpha = 0
	private _pulse = 1
	private _showRing = false
	private _bearing = 0
	private _label = ""
	private _sublabel = ""

	/** Reticle (and rotation ring) opacity */
	public set alpha(value: number) {
		if(this._alpha !== value) {
			this._alpha = value
			this.setNeedsUpdate()
		}
	}

	/** Coordinate readout opacity, animated independently of the reticle */
	public set readoutAlpha(value: number) {
		if(this._readoutAlpha !== value) {
			this._readoutAlpha = value
			this.setNeedsUpdate()
		}
	}

	/** Scale multiplier for the settle pulse */
	public set pulse(value: number) {
		if(this._pulse !== value) {
			this._pulse = value
			this.setNeedsUpdate()
		}
	}

	/** Show the rotation ring with a north tick during bearing changes */
	public set showRing(value: boolean) {
		if(this._showRing !== value) {
			this._showRing = value
			this.setNeedsUpdate()
		}
	}

	public set bearing(value: number) {
		if(this._bearing !== value) {
			this._bearing = value
			if(this._showRing) {
				this.setNeedsUpdate()
			}
		}
	}

	public set label(value: string) {
		if(this._label !== value) {
			this._label = value
			this.setNeedsUpdate()
		}
	}

	public get labelText(): string {
		return this._label
	}

	public get sublabelText(): string {
		return this._sublabel
	}

	/** Rough terrain elevation readout shown under the coordinates */
	public set sublabel(value: string) {
		if(this._sublabel !== value) {
			this._sublabel = value
			this.setNeedsUpdate()
		}
	}

	override render(): void {
		const a = this._alpha
		const ra = this._readoutAlpha
		if(a <= 0 && ra <= 0) return

		const cx = this.width / 2
		const cy = cReticleCenterY

		// Coordinate readout under the reticle, with the rough terrain
		// elevation on the line beneath it
		if(ra > 0) {
			if(this._label) {
				const [tw] = this.measureText(this._label, cLabelFont, true)
				this.drawText(this._label, cLabelFont, rgba(1, 1, 1, ra), (this.width - tw) / 2, cy + 20, rgba(0, 0, 0, ra * 0.8))
			}
			if(this._sublabel) {
				const [sw] = this.measureText(this._sublabel, cSublabelFont, true)
				this.drawText(this._sublabel, cSublabelFont, rgba(1, 1, 1, ra * 0.9), (this.width - sw) / 2, cy + 38, rgba(0, 0, 0, ra * 0.8))
			}
		}

		if(a <= 0) return

		const s = this._pulse
		const arm = 9 * s
		const gap = 3.5 * s

		// Dark halo under white ink keeps the reticle legible over bright
		// terrain (clouds, snow, ice)
		const halo = rgba(0, 0, 0, a * 0.9)
		const ink = rgba(1, 1, 1, a)

		// Reticle arms with a center gap so the pivot point stays visible
		const arms = (c: color, w: number): void => {
			this.beginPath()
				.line(cx, cy - arm, cx, cy - gap)
				.line(cx, cy + gap, cx, cy + arm)
				.line(cx - arm, cy, cx - gap, cy)
				.line(cx + gap, cy, cx + arm, cy)
				.stroke(c, w, "round", "round")
		}
		arms(halo, 3)
		arms(ink, 1.5)

		// Center dot marking the exact pivot point
		this.beginPath().circle(cx, cy, 2).fill(halo)
		this.beginPath().circle(cx, cy, 1.1).fill(ink)

		// Rotation ring with a north tick while the bearing is changing
		if(this._showRing) {
			const r = 14 * s
			this.beginPath().circle(cx, cy, r).stroke(halo, 3, "round", "round")
			this.beginPath().circle(cx, cy, r).stroke(ink, 1.5, "round", "round")

			// Tick points toward north: at bearing 0 it sits at the top and
			// counter-rotates as the camera bearing changes
			const na = -this._bearing - Math.PI / 2
			const tx = Math.cos(na)
			const ty = Math.sin(na)
			this.beginPath()
				.line(cx + tx * (r - 3), cy + ty * (r - 3), cx + tx * (r + 3), cy + ty * (r + 3))
				.stroke(halo, 4, "round", "round")
			this.beginPath()
				.line(cx + tx * (r - 3), cy + ty * (r - 3), cx + tx * (r + 3), cy + ty * (r + 3))
				.stroke(ink, 2, "round", "round")
		}
	}
}
